import React, { createContext, useState, useEffect } from "react";
import axiosInstance from "../config/axios";
import { useAuth } from "./authContext";

export const ChatContext = createContext();

const MODEL_CONFIG = {
  "TSG Model v1": {
    id: "gpt-3.5-turbo",
    desc: "Fast & default",
  },
  "TSG Plus": {
    id: "gpt-4o-mini",
    desc: "Balanced power",
  },
  "TSG Pro": {
    id: "gpt-4",
    desc: "Maximum reasoning",
  },
};
const MODEL_STORAGE_KEY = "corporate_ai_model";
const DEFAULT_MODEL = "TSG Model v1";

// 🎯 Generate meaningful title from message content
const generateChatTitle = (content) => {
  if (!content || content.trim().length === 0) return "New Chat";
  
  // Remove common prefixes and clean up
  const cleanContent = content
    .replace(/^(help|how|what|why|when|where|can|please|hey|hi|hello)\s+/i, "")
    .trim();
  
  // Take first 8-10 words for a better title, or up to 50 characters
  const words = cleanContent.split(/\s+/);
  let title = words.slice(0, 10).join(" ");
  
  if (title.length > 50) {
    title = title.substring(0, 47) + "...";
  } else if (words.length > 10) {
    title += "...";
  }
  
  // Capitalize first letter
  title = title.charAt(0).toUpperCase() + title.slice(1);
  
  return title;
};

export const ChatProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  // Helper for safe localStorage
  const safeLoad = (key) => {
    try {
      const val = localStorage.getItem(key);
      return val ? JSON.parse(val) : [];
    } catch {
      localStorage.removeItem(key);
      return [];
    }
  };

  // Get storage keys based on current user
  const getStorageKeys = () => {
    if (!user?.email) {
      return {
        projects: "corporate_ai_projects",
        chats: "corporate_ai_chats",
        currentChat: "corporate_ai_currentChat",
        currentProject: "corporate_ai_currentProject",
      };
    }
    const userKey = user.email.toLowerCase().replace(/[^a-z0-9]/g, '_');
    return {
      projects: `corporate_ai_projects_${userKey}`,
      chats: `corporate_ai_chats_${userKey}`,
      currentChat: `corporate_ai_currentChat_${userKey}`,
      currentProject: `corporate_ai_currentProject_${userKey}`,
    };
  };

  const [projects, setProjects] = useState([]);
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [archivedChats, setArchivedChats] = useState([]);

  // Model selection state
  const [selectedModel, setSelectedModel] = useState(() => {
    try {
      return localStorage.getItem(MODEL_STORAGE_KEY) || DEFAULT_MODEL;
    } catch {
      return DEFAULT_MODEL;
    }
  });

  const updateSelectedModel = (modelName) => {
    if (!MODEL_CONFIG[modelName]) return;
    setSelectedModel(modelName);
    try {
      localStorage.setItem(MODEL_STORAGE_KEY, modelName);
    } catch {
      // Ignore storage errors
    }
  };

  // 🧠 NEW: processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [abortController, setAbortController] = useState(null);

  const persist = (key, data) => {
    if (!isAuthenticated || !user?.email) return;
    localStorage.setItem(key, JSON.stringify(data));
  };

  // Reset chat state when user changes (logout/login)
  useEffect(() => {
    if (!isAuthenticated || !user?.email) {
      setProjects([]);
      setChats([]);
      setCurrentChat(null);
      setCurrentProject(null);
      setMessages([]);
    } else {
      // Load user-specific data when user logs in
      const keys = getStorageKeys();
      const loadedProjects = safeLoad(keys.projects);
      const loadedChats = safeLoad(keys.chats);
      setProjects(loadedProjects);
      setChats(loadedChats);
      
      const savedChat = localStorage.getItem(keys.currentChat);
      if (savedChat) {
        const chatId = Number(savedChat);
        setCurrentChat(chatId);
        const chat = loadedChats.find((c) => c.id === chatId);
        setMessages(chat?.messages || []);
      } else {
        setCurrentChat(null);
        setMessages([]);
      }
      
      const savedProject = localStorage.getItem(keys.currentProject);
      if (savedProject) {
        setCurrentProject(Number(savedProject));
      } else {
        setCurrentProject(null);
      }
    }
  }, [isAuthenticated, user?.email]);

  // 🆕 Create Project
  const createProject = async ({ name, desc }) => {
    if (!isAuthenticated || !user?.email) {
      throw new Error("User must be authenticated to create projects");
    }
    
    const keys = getStorageKeys();
    const project = {
      id: Date.now(),
      name,
      description: desc || "",
      createdAt: new Date().toISOString(),
    };
    
    // Try to save to backend
    try {
      const response = await axiosInstance.post("/api/chat/projects", {
        name,
        description: desc || "",
      });
      if (response.data && response.data.id) {
        project.id = response.data.id;
        project.created_at = response.data.created_at;
      }
    } catch (error) {
      console.warn("Failed to save project to backend:", error);
    }
    
    const updated = [...projects, project];
    setProjects(updated);
    persist(keys.projects, updated);
    setCurrentProject(project.id);
    return project;
  };

  // 🆕 Start New Chat
  const startNewChat = (projectId = null) => {
    if (!isAuthenticated || !user?.email) {
      throw new Error("User must be authenticated to create chats");
    }
    
    const keys = getStorageKeys();
    const effectiveProjectId = projectId || currentProject;
    const newChat = {
      id: Date.now(),
      title: "New Chat",
      messages: [],
      projectId: effectiveProjectId,
      createdAt: new Date().toISOString(),
    };
    const updated = [...chats, newChat];
    setChats(updated);
    setCurrentChat(newChat.id);
    setMessages([]);
    persist(keys.chats, updated);
    // Save current chat to localStorage
    localStorage.setItem(keys.currentChat, String(newChat.id));
    if (effectiveProjectId) {
      localStorage.setItem(keys.currentProject, String(effectiveProjectId));
    }
    // Ensure current project is set when creating chat in a project
    if (effectiveProjectId && !currentProject) {
      setCurrentProject(effectiveProjectId);
    }
    return newChat.id;
  };

  // ➕ Add Message + show processing text
  const addMessage = async (msg) => {
    let activeChatId = currentChat;
    if (!activeChatId) activeChatId = startNewChat(currentProject);

    const message = {
      role: msg.role || "user",
      content: msg.content || "",
      files: msg.files || [],
      timestamp: new Date().toISOString(),
    };

    const updatedMessages = [...messages, message];
    setMessages(updatedMessages);

    setChats((prevChats) => {
      const updatedChats = prevChats.map((chat) =>
        chat.id === activeChatId ? { ...chat, messages: updatedMessages } : chat
      );

      // 🪄 Auto-title first message with better naming
      const targetChat = updatedChats.find((c) => c.id === activeChatId);
      if (
        targetChat &&
        (targetChat.title === "New Chat" || !targetChat.title) &&
        msg.role === "user"
      ) {
        const newTitle = generateChatTitle(msg.content);
        targetChat.title = newTitle;
        console.log("Generated title:", newTitle);

        // Try to update title on backend
        if (typeof activeChatId === "number" && activeChatId > 1000000000000) {
          // This is a temporary timestamp ID, don't send to backend yet
          // It will be updated when the message is sent and response comes back
        } else {
          axiosInstance.put(`/api/chat/chats/${activeChatId}/title`, { title: newTitle })
            .catch(err => console.warn("Failed to update chat title on backend:", err));
        }
      }

      const keys = getStorageKeys();
      persist(keys.chats, updatedChats);
      return [...updatedChats]; // Return new array to trigger state update
    });

    // 🧠 Simulate "Thinking / Analyzing / Generating" while processing
    if (msg.role === "user") {
      setIsProcessing(true);
      const controller = new AbortController();
      setAbortController(controller);
      
      const steps = ["🧠 Thinking...", "🔍 Analyzing...", "✨ Generating..."];
      let i = 0;
      setProcessingMessage(steps[i]);
      const interval = setInterval(() => {
        i = (i + 1) % steps.length;
        setProcessingMessage(steps[i]);
      }, 1200);

      try {
        // Simulated response delay (replace with your AI call later)
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(resolve, 3000);
          controller.signal.addEventListener('abort', () => {
            clearTimeout(timeout);
            reject(new Error('Aborted'));
          });
        });

        clearInterval(interval);
        // setIsProcessing(false); // Keep processing until API response comes

        // 🔄 Get real AI response from backend API
        let aiReply = "";
        let newBackendId = null;
        try {
          // Build conversation history - send last user message and context
          const maxMessages = 10;
          const recentMessages = updatedMessages.slice(-maxMessages);
          
          // Get the last user message content
          const lastUserMessage = recentMessages
            .filter(msg => msg.role === "user")
            .pop()?.content || msg.content;

          // Call backend chat API
          const targetModel =
            MODEL_CONFIG[selectedModel]?.id || MODEL_CONFIG[DEFAULT_MODEL].id;

          const response = await axiosInstance.post("/api/chat", {
            message: lastUserMessage,
            chatId: (typeof activeChatId === 'number' && activeChatId < 1000000000000) ? activeChatId : null,
            projectId: currentProject,
            model: targetModel,
            conversationHistory: recentMessages.map(msg => ({
              role: msg.role,
              content: msg.content.substring(0, 2000), // Limit message length
            })),
          }, {
            signal: controller.signal
          });

          if (response.data && response.data.reply) {
            aiReply = response.data.reply;
            
            // Update chatId if it was created by the backend
            if (response.data.chatId && (!activeChatId || activeChatId > 1000000000000)) {
              newBackendId = response.data.chatId;
              
              // If we just got a real ID from the backend, and we have a title, update it
              const generatedTitle = generateChatTitle(lastUserMessage);
              axiosInstance.put(`/api/chat/chats/${newBackendId}/title`, { title: generatedTitle })
                .catch(err => console.warn("Failed to update chat title on backend:", err));
            }
          } else {
            throw new Error("No response from server");
          }
        } catch (error) {
          if (error.name === 'AbortError' || error.message === 'Aborted') {
            console.log("Chat request aborted");
            setIsProcessing(false);
            setProcessingMessage("");
            setAbortController(null);
            return;
          }
          console.error("❌ Chat API error:", error);
          aiReply = `⚠️ Error: ${error.response?.data?.error || error.message || "Failed to get AI response. Please try again."}`;
        }

        // Add assistant's response
        const assistantMessage = {
          id: Date.now() + 1, // temporary ID
          role: "assistant",
          content: aiReply,
          timestamp: new Date().toISOString(),
        };

        const finalMessages = [...updatedMessages, assistantMessage];
        setMessages(finalMessages);

        setChats((prevChats) => {
          const newChats = prevChats.map((chat) => {
            if (chat.id === activeChatId) {
              return { 
                ...chat, 
                id: newBackendId || chat.id,
                messages: finalMessages 
              };
            }
            return chat;
          });
          
          if (newBackendId) {
            setCurrentChat(newBackendId);
            const keys = getStorageKeys();
            localStorage.setItem(keys.currentChat, String(newBackendId));
          }
          
          const keys = getStorageKeys();
          persist(keys.chats, newChats);
          return [...newChats]; // Return new array to trigger state update
        });
        
        // Finalize state
        setIsProcessing(false);
        setProcessingMessage("");
        setAbortController(null);

      } catch (err) {
        clearInterval(interval);
        setIsProcessing(false);
        setProcessingMessage("");
        setAbortController(null);
        if (err.message !== 'Aborted') {
          console.error("Error in addMessage:", err);
        }
      }
    }
  };

  const stopGeneration = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsProcessing(false);
      setProcessingMessage("");
    }
  };

  const editMessage = async (messageIndex, newContent) => {
    const updatedMessages = [...messages];
    const msgToEdit = updatedMessages[messageIndex];
    if (!msgToEdit) return;

    msgToEdit.content = newContent;
    setMessages(updatedMessages);

    // Update locally in chats
    setChats(prev => {
      const updatedChats = prev.map(chat => 
        chat.id === currentChat ? { ...chat, messages: updatedMessages } : chat
      );
      const keys = getStorageKeys();
      persist(keys.chats, updatedChats);
      return [...updatedChats];
    });

    // Update on backend if message has an ID
    if (msgToEdit.id && typeof msgToEdit.id === 'number' && msgToEdit.id < 1000000000000) {
      try {
        await axiosInstance.put(`/api/chat/messages/${msgToEdit.id}`, { content: newContent });
      } catch (err) {
        console.warn("Failed to update message on backend:", err);
      }
    }
  };

  const deleteMessage = async (messageIndex) => {
    const msgToDelete = messages[messageIndex];
    const updatedMessages = messages.filter((_, i) => i !== messageIndex);
    setMessages(updatedMessages);

    // Update locally in chats
    setChats(prev => {
      const updatedChats = prev.map(chat => 
        chat.id === currentChat ? { ...chat, messages: updatedMessages } : chat
      );
      const keys = getStorageKeys();
      persist(keys.chats, updatedChats);
      return [...updatedChats];
    });

    // Delete from backend if message has an ID
    if (msgToDelete?.id && typeof msgToDelete.id === 'number' && msgToDelete.id < 1000000000000) {
      try {
        await axiosInstance.delete(`/api/chat/messages/${msgToDelete.id}`);
      } catch (err) {
        console.warn("Failed to delete message on backend:", err);
      }
    }
  };

  const rateResponse = async (messageIndex, rating) => {
    const updatedMessages = [...messages];
    const msgToRate = updatedMessages[messageIndex];
    if (!msgToRate || msgToRate.role !== 'assistant') return;

    msgToRate.rating = rating; // 1 for thumbs up, -1 for thumbs down, 0 for neutral
    setMessages(updatedMessages);

    // Update locally in chats
    setChats(prev => {
      const updatedChats = prev.map(chat => 
        chat.id === currentChat ? { ...chat, messages: updatedMessages } : chat
      );
      const keys = getStorageKeys();
      persist(keys.chats, updatedChats);
      return [...updatedChats];
    });

    // Rate on backend if message has an ID
    if (msgToRate.id && typeof msgToRate.id === 'number' && msgToRate.id < 1000000000000) {
      try {
        await axiosInstance.post(`/api/chat/messages/${msgToRate.id}/rate`, { rating });
      } catch (err) {
        console.warn("Failed to rate message on backend:", err);
      }
    }
  };

  const regenerateResponse = async (messageIndex) => {
    const msgToRegenerate = messages[messageIndex];
    if (!msgToRegenerate || msgToRegenerate.role !== 'assistant') return;

    // We want to regenerate from the message BEFORE this one (the user prompt)
    const userPromptIndex = messageIndex - 1;
    if (userPromptIndex < 0 || messages[userPromptIndex].role !== 'user') return;

    const userPrompt = messages[userPromptIndex].content;

    // Remove the old assistant message (and any subsequent messages if they exist)
    const updatedMessages = messages.slice(0, messageIndex);
    setMessages(updatedMessages);

    // Update locally in chats
    setChats(prev => {
      const updatedChats = prev.map(chat => 
        chat.id === currentChat ? { ...chat, messages: updatedMessages } : chat
      );
      const keys = getStorageKeys();
      persist(keys.chats, updatedChats);
      return [...updatedChats];
    });

    // Re-trigger addMessage with the same user prompt
    // This will handle API call, thinking state, and appending the new assistant message
    await addMessage({
      role: "user",
      content: userPrompt,
      isRegenerate: true // Flag to potentially handle differently if needed
    });
  };

  // 🔁 Switch Chat
  const switchChat = (chatId) => {
    if (!isAuthenticated || !user?.email) return;
    
    const keys = getStorageKeys();
    const chat = chats.find((c) => c.id === chatId);
    if (chat) {
      setCurrentChat(chat.id);
      setCurrentProject(chat.projectId);
      setMessages(chat.messages || []);
      // Save current chat to localStorage for persistence on refresh
      localStorage.setItem(keys.currentChat, String(chat.id));
      localStorage.setItem(keys.currentProject, String(chat.projectId || ""));
    }
  };

  // 🗑 Delete Chat
  const deleteChat = (chatId) => {
    if (!isAuthenticated || !user?.email) return;
    
    const keys = getStorageKeys();
    setChats((prevChats) => {
      const updated = prevChats.filter((c) => c.id !== chatId);
      persist(keys.chats, updated);
      return updated;
    });

    if (currentChat === chatId) {
      setCurrentChat(null);
      setMessages([]);
      localStorage.removeItem(keys.currentChat);
    }
  };

  // 📦 Archive Chat
  const archiveChat = async (chatId) => {
    if (!isAuthenticated || !user?.email) return;

    // Try to update on backend
    try {
      if (typeof chatId === "number" && chatId < 1000000000000) {
        await axiosInstance.put(`/api/chat/chats/${chatId}/archive`);
      }
    } catch (err) {
      console.warn("Failed to archive chat on backend:", err);
    }

    const keys = getStorageKeys();
    setChats((prevChats) => {
      const chatToArchive = prevChats.find((c) => c.id === chatId);
      if (chatToArchive) {
        const updated = prevChats.filter((c) => c.id !== chatId);
        persist(keys.chats, updated);
        
        // Move to archivedChats locally
        setArchivedChats((prev) => [...prev, { ...chatToArchive, archived: true }]);
        
        return updated;
      }
      return prevChats;
    });

    if (currentChat === chatId) {
      setCurrentChat(null);
      setMessages([]);
      localStorage.removeItem(keys.currentChat);
    }
  };

  // 🧹 Clear All Chats
  const clearAllChats = () => {
    if (!isAuthenticated || !user?.email) return;
    
    const keys = getStorageKeys();
    setChats([]);
    persist(keys.chats, []);
    setCurrentChat(null);
    setMessages([]);
    localStorage.removeItem(keys.currentChat);
    // Explicitly notify success or trigger UI update if needed
    console.log("Chat history cleared for", user.email);
  };

  // 📂 Filter Chats by Project
  const getChatsByProject = (projectId) =>
    projectId ? chats.filter((c) => c.projectId === projectId) : chats;

  // 🔄 Sync messages when chat changes
  useEffect(() => {
    if (currentChat) {
      const chat = chats.find((c) => c.id === currentChat);
      if (chat) setMessages(chat.messages || []);
    }
  }, [currentChat, chats]);

  return (
    <ChatContext.Provider
      value={{
        chats,
        messages,
        projects,
        currentChat,
        currentProject,
        startNewChat,
        addMessage,
        switchChat,
        deleteChat,
        archiveChat,
        clearAllChats,
        createProject,
        getChatsByProject,
        setCurrentProject,
        // 👇 new states for spinner
        isProcessing,
        processingMessage,
        stopGeneration,
        editMessage,
        deleteMessage,
        rateResponse,
        regenerateResponse,
        // model controls
        selectedModel,
        setSelectedModel: updateSelectedModel,
        modelOptions: MODEL_CONFIG,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
